import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'
import LogoutRoundedIcon from '@mui/icons-material/LogoutRounded'
import WidgetsRoundedIcon from '@mui/icons-material/WidgetsRounded'
import Box from '@mui/joy/Box'
import Divider from '@mui/joy/Divider'
import GlobalStyles from '@mui/joy/GlobalStyles'
import IconButton from '@mui/joy/IconButton'
import List from '@mui/joy/List'
import ListItem from '@mui/joy/ListItem'
import ListItemButton, { listItemButtonClasses } from '@mui/joy/ListItemButton'
import ListItemContent from '@mui/joy/ListItemContent'
import BrowseGalleryIcon from '@mui/icons-material/BrowseGallery'
import Sheet from '@mui/joy/Sheet'
import Typography from '@mui/joy/Typography'
import { useAuth } from 'hooks/auth'
import * as React from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import ColorSchemeToggle from '../../components/ColorSchemeToggle'
import { getStoredEnvironment } from 'setup/SetupAxios'
import { hasAdminPageAccess, isMobileHomeOnly, MOBILE_HOME_EDITOR_PATHS } from 'utils/roles'
import {
    Email,
    InfoRounded,
    Schedule,
    Receipt,
    Sync,
    SmartToy,
    Telegram,
    LocalShipping,
    SwapVert,
    Insights,
    Dns,
    Person,
    Description,
    Timeline,
    ManageSearch,
    Assessment,
    History,
    DatasetLinked,
    Psychology,
    SettingsApplications,
    LocalOffer as LocalOfferIcon,
    Notifications,
    PhoneAndroid,
    Extension,
    Tune,
    AccountTree,
    SupportAgent,
    VpnKey,
    Campaign,
    Cable,
    VerifiedUser,
    Storefront,
    ManageAccounts,
    LibraryBooks,
    Groups,
    MyLocation,
    Security,
    SystemUpdate,
} from '@mui/icons-material'

interface NavItem {
    name: string
    icon: React.ReactNode
    // On a group, the route prefix that keeps it expanded; groups without their own routes leave it out.
    path?: string
    // Hidden from users without admin/PM access (hasAdminPageAccess). The route is also wrapped in
    // <ProtectedRoute> (dashboard/routes.tsx), so a non-admin can't reach it by URL either.
    adminOnly?: boolean
    children?: NavItem[]
}

const ROUTER_LIST: NavItem[] = [
    {
        name: 'Applications',
        icon: <WidgetsRoundedIcon />,
        path: '/applications',
        adminOnly: true,
    },
    {
        // Audit surface for the load data pipeline (ADR-032 writer, ADR-033 consumer).
        // Read-only, cross-tenant, and operator-gated server-side — open to every
        // gluon operator, not just admin/PM, so it is deliberately NOT adminOnly.
        // Its routes are mounted ungated in pages/routes.tsx,
        // so hiding the link only hid a page the URL already reached.
        name: 'Load Data Pipeline',
        icon: <AccountTree />,
        path: '/load-pipeline',
        // Ordered as the data flows — writer, then topic, then consumer — with
        // a short stage prefix (W-/T-/C-) rather than the spelled-out prefix
        // this used to carry: five sidebar entries for one consumer read as
        // five things, when they were five views of one. Market Intel is now a
        // single entry whose page holds its own tabs (health / lane prices /
        // heat map / lane explorer / SQL performance) — a second consumer gets
        // its own C- entry the same way, not more tabs bolted onto this one.
        children: [
            {
                name: 'Overview',
                icon: <Insights />,
                path: '/load-pipeline/overview',
            },
            {
                name: 'W-DAT Observer',
                icon: <Dns />,
                path: '/load-pipeline/writer/dat',
            },
            {
                name: 'W-Sylectus Email',
                icon: <Dns />,
                path: '/load-pipeline/writer/sylectus',
            },
            {
                name: 'W-Partner Parade',
                icon: <Dns />,
                path: '/load-pipeline/writer/parade',
            },
            {
                name: 'W-Partner USPS',
                icon: <Dns />,
                path: '/load-pipeline/writer/usps',
            },
            { name: 'W-Observer Boards', icon: <Dns />, path: '/load-pipeline/writer/boards' },
            {
                name: 'W-Ratecon',
                icon: <Dns />,
                path: '/load-pipeline/writer/ratecon',
            },
            {
                name: 'W-Broker Reply',
                icon: <Dns />,
                path: '/load-pipeline/writer/broker-reply',
            },
            {
                name: 'X-Note Extractor',
                icon: <Dns />,
                path: '/load-pipeline/notes',
            },
            {
                name: 'T-Observations',
                icon: <AccountTree />,
                path: '/load-pipeline/topic',
            },
            {
                name: 'C-Market Intel',
                icon: <Timeline />,
                path: '/load-pipeline/market-intel/health',
            },
            {
                name: 'C-Search',
                icon: <ManageSearch />,
                path: '/load-pipeline/search/probe',
            },
        ],
    },
    {
        // Audit surface for the broker data pipeline (ADR-047), laid out like the load pipeline's and gated the same way:
        // read-only, operator-gated server-side, so not adminOnly. Writers not yet built are listed on the Overview.
        name: 'Broker Data Pipeline',
        icon: <AccountTree />,
        path: '/broker-pipeline',
        children: [
            { name: 'Overview', icon: <Insights />, path: '/broker-pipeline/overview' },
            { name: 'W-FMCSA', icon: <Dns />, path: '/broker-pipeline/writer/fmcsa' },
            { name: 'Quarantine notes', icon: <Dns />, path: '/broker-pipeline/quarantine' },
            { name: 'T-Broker Updates', icon: <AccountTree />, path: '/broker-pipeline/topic' },
            { name: 'C-Broker Search', icon: <ManageSearch />, path: '/broker-pipeline/consumer/refusals' },
        ],
    },
    {
        name: 'Document Pipeline',
        icon: <Receipt />,
        path: '/document-pipeline',
        children: [
            {
                name: 'Pipeline Audit',
                icon: <Insights />,
                path: '/document-pipeline/audit',
            },
            {
                name: 'Document Jobs',
                icon: <Description />,
                path: '/document-pipeline/document-jobs',
            },
            {
                name: 'Document Processing Test',
                icon: <Tune />,
                path: '/document-pipeline/document-processing-test',
            },
            {
                name: 'Devices & Keys',
                icon: <VpnKey />,
                path: '/document-pipeline/devices',
            },
            {
                name: 'Ratecon Fine-Tuning',
                icon: <DatasetLinked />,
                path: '/ratecon-fine-tuning',
            },
        ],
    },
    {
        name: 'Billing',
        icon: <Sync />,
        path: '/billing',
        children: [
            { name: 'Customers', icon: <Person />, path: '/billing' },
            { name: 'Trials', icon: <Schedule />, path: '/billing/trials' },
            { name: 'Mobile offers', icon: <Campaign />, path: '/billing/mobile-offers' },
            { name: 'Influencers', icon: <Campaign />, path: '/billing/influencers' },
            { name: 'Payment Links', icon: <DatasetLinked />, path: '/payment-links' },
            { name: 'Promo Codes', icon: <LocalOfferIcon />, path: '/promo-codes' },
            { name: 'Instant Pay', icon: <Sync />, path: '/instant-pay-fee' },
            { name: 'Growth Campaign', icon: <LocalOfferIcon />, path: '/growth-campaign' },
        ],
    },
    {
        name: 'MC Verification',
        icon: <VerifiedUser />,
        path: '/mc-verification',
        adminOnly: true,
    },
    {
        name: 'Box Truck',
        icon: <LocalShipping />,
        children: [
            {
                name: 'Drivers',
                icon: <LocalShipping />,
                path: '/box-truck-drivers',
            },
            {
                name: 'Bid Analytics',
                icon: <Insights />,
                path: '/box-truck-bid-analytics',
            },
            {
                name: 'Pricing Analytics',
                icon: <Insights />,
                path: '/box-truck-pricing-analytics',
            },
            {
                name: 'Dispatchers Bids',
                icon: <Insights />,
                path: '/dispatcher-bids',
            },
            {
                name: 'Drivers Bids',
                icon: <Insights />,
                path: '/driver-bids',
            },
            {
                name: 'ZIP Check-ins',
                icon: <Insights />,
                path: '/box-truck-zip-analytics',
            },
            {
                name: 'Load Offers',
                icon: <Insights />,
                path: '/box-truck-offer-analytics',
            },
            // The same page as Access & Security → Operator Access, listed twice on purpose. It is
            // where Box Truck access is granted — the boxTruck, dispatchHelp and pricing ticks — so it
            // belongs beside the pages it governs as well as beside the other access controls.
            {
                name: 'Operator Access',
                icon: <SupportAgent />,
                path: '/operators',
            },
        ],
    },
    {
        name: 'Telegram Watcher',
        icon: <Telegram />,
        path: '/telegram-watcher',
    },
    {
        name: 'Oracle',
        icon: <Psychology />,
        path: '/crm',
        children: [
            {
                name: 'Setup',
                icon: <Tune />,
                path: '/crm/setup',
            },
            {
                name: 'Pipeline',
                icon: <AccountTree />,
                path: '/crm/pipeline',
            },
            {
                name: 'Agent',
                icon: <SupportAgent />,
                path: '/crm/agent',
            },
            {
                name: 'MCP Access',
                icon: <VpnKey />,
                path: '/crm/mcp',
            },
        ],
    },
    {
        name: 'Brokers',
        icon: <Storefront />,
        path: '/brokers',
        children: [
            {
                name: 'Portal Accounts',
                icon: <ManageAccounts />,
                path: '/brokers/portal-accounts',
            },
            {
                name: 'Broker Catalog',
                icon: <LibraryBooks />,
                path: '/brokers/catalog',
            },
        ],
    },
    {
        name: 'Mailbox',
        icon: <Email />,
        path: '/mailbox',
    },
    {
        name: 'DAT',
        icon: <MyLocation />,
        children: [
            { name: 'DAT Token Usage', icon: <VpnKey />, path: '/dat-token-usage', adminOnly: true },
            { name: 'DAT Token Protection', icon: <VpnKey />, path: '/dat-token-protection', adminOnly: true },
        ],
    },
    {
        name: 'Product Config',
        icon: <SettingsApplications />,
        children: [
            {
                name: 'Mobile',
                icon: <PhoneAndroid />,
                children: [
                    { name: 'Mobile Version Policy', icon: <PhoneAndroid />, path: '/mobile-version-policy' },
                    { name: 'Live DAT Search', icon: <MyLocation />, path: '/live-dat-search' },
                    { name: 'Mobile Features', icon: <PhoneAndroid />, path: '/mobile-features' },
                    { name: 'Mobile Home Layout', icon: <PhoneAndroid />, path: '/mobile-home-layout' },
                    { name: 'Mobile Promo Pages', icon: <PhoneAndroid />, path: '/mobile-promo-pages' },
                    { name: 'Mobile Load Access', icon: <LocalShipping />, path: '/mobile-load-access' },
                    { name: 'Mobile Load Sort', icon: <SwapVert />, path: '/mobile-load-sort' },
                    { name: 'Mobile Partner Offers', icon: <Campaign />, path: '/mobile-partner-offers' },
                    { name: 'Mobile Rate Check', icon: <Insights />, path: '/mobile-rate-check' },
                    { name: 'Mobile OTA Updates', icon: <SystemUpdate />, path: '/mobile-ota-updates' },
                    { name: 'Push Notifications', icon: <Notifications />, path: '/push-notifications' },
                    { name: 'Search Cohorts', icon: <Groups />, path: '/search-cohorts' },
                ],
            },
            { name: 'Extension Version Policy', icon: <Extension />, path: '/extension-version-policy' },
            { name: 'System Config', icon: <SettingsApplications />, path: '/system-config' },
        ],
    },
    {
        name: 'Logs',
        icon: <History />,
        children: [
            { name: 'Truck History', icon: <LocalShipping />, path: '/truck-history' },
            { name: 'ETA History', icon: <BrowseGalleryIcon />, path: '/eta-history' },
            { name: 'Notification Logs', icon: <InfoRounded />, path: '/notification-logs' },
            { name: 'Agent Activity Logs', icon: <SmartToy />, path: '/agent-activity-logs' },
            { name: 'ELD Requests', icon: <Cable />, path: '/eld-integration-requests' },
            { name: 'Invoice Reports', icon: <Assessment />, path: '/invoice-reports' },
            { name: 'Schedulers', icon: <Schedule />, path: '/schedulers' },
        ],
    },
    {
        name: 'Access & Security',
        icon: <Security />,
        children: [
            { name: 'Operator Access', icon: <SupportAgent />, path: '/operators' },
            {
                name: 'Proxy',
                icon: <VpnKey />,
                path: '/proxy-allowlist',
                children: [
                    { name: 'Allowlist', icon: <VerifiedUser />, path: '/proxy-allowlist' },
                    { name: 'VPN Users', icon: <Person />, path: '/proxy-allowlist/vpn-users' },
                ],
            },
            { name: 'DNS Monitoring', icon: <Dns />, path: '/dns-monitoring' },
        ],
    },
]

function filterAdminOnly(items: NavItem[]): NavItem[] {
    return items.flatMap((item) => {
        if (item.adminOnly) return []
        if (!item.children) return [item]
        const children = filterAdminOnly(item.children)
        return children.length ? [{ ...item, children }] : []
    })
}

function filterToPaths(items: NavItem[], paths: string[]): NavItem[] {
    return items.flatMap((item) => {
        if (!item.children) return item.path && paths.includes(item.path) ? [item] : []
        const children = filterToPaths(item.children, paths)
        return children.length ? [{ ...item, children }] : []
    })
}

function leafPaths(items: NavItem[]): string[] {
    return items.flatMap((item) => (item.children ? leafPaths(item.children) : item.path ? [item.path] : []))
}

// The longest link path the current URL sits under, so '/billing/trials' selects Trials, not Customers.
function findActivePath(items: NavItem[], pathname: string): string | undefined {
    return leafPaths(items)
        .filter((path) => pathname === path || pathname.startsWith(`${path}/`))
        .sort((a, b) => b.length - a.length)[0]
}

function containsPath(item: NavItem, path: string | undefined): boolean {
    if (!path) return false
    return item.children ? item.children.some((child) => containsPath(child, path)) : item.path === path
}

interface Props {
    defaultExpanded?: boolean
    children: React.ReactNode
    renderToggle: (params: { open: boolean; setOpen: React.Dispatch<React.SetStateAction<boolean>> }) => React.ReactNode
}
function Toggler({ defaultExpanded = false, renderToggle, children }: Props) {
    const [open, setOpen] = React.useState(defaultExpanded)
    return (
        <React.Fragment>
            {renderToggle({ open, setOpen })}
            <Box
                sx={[
                    {
                        display: 'grid',
                        transition: '0.2s ease',
                        '& > *': {
                            overflow: 'hidden',
                        },
                    },
                    open ? { gridTemplateRows: '1fr' } : { gridTemplateRows: '0fr' },
                ]}
            >
                {children}
            </Box>
        </React.Fragment>
    )
}

function SidebarItem({ item, pathname, activePath }: { item: NavItem; pathname: string; activePath: string | undefined }) {
    const navigate = useNavigate()

    if (!item.children) {
        return (
            <ListItem>
                <ListItemButton onClick={() => item.path && navigate(item.path)} selected={item.path === activePath}>
                    {item.icon}
                    <ListItemContent>
                        <Typography level="title-sm">{item.name}</Typography>
                    </ListItemContent>
                </ListItemButton>
            </ListItem>
        )
    }

    const expanded = containsPath(item, activePath) || (!!item.path && pathname.startsWith(item.path))

    return (
        <ListItem nested>
            <Toggler
                defaultExpanded={expanded}
                renderToggle={({ open, setOpen }) => (
                    <ListItemButton onClick={() => setOpen(!open)}>
                        {item.icon}
                        <ListItemContent>
                            <Typography level="title-sm">{item.name}</Typography>
                        </ListItemContent>
                        <KeyboardArrowDownIcon sx={{ transform: open ? 'rotate(180deg)' : 'none' }} />
                    </ListItemButton>
                )}
            >
                <List sx={{ gap: 0.5 }}>
                    {item.children.map((child) => (
                        <SidebarItem key={child.name} item={child} pathname={pathname} activePath={activePath} />
                    ))}
                </List>
            </Toggler>
        </ListItem>
    )
}

export default function Sidebar() {
    const { logout } = useAuth()
    const navigate = useNavigate()
    const { pathname } = useLocation()
    const authData = localStorage.getItem('auth_data')

    // In production: hide admin-only pages from users without admin or PM role.
    // In development: show everything to everyone.
    const filteredRouterList = React.useMemo(() => {
        if (isMobileHomeOnly()) return filterToPaths(ROUTER_LIST, MOBILE_HOME_EDITOR_PATHS)
        return hasAdminPageAccess() ? ROUTER_LIST : filterAdminOnly(ROUTER_LIST)
    }, [])
    const activePath = findActivePath(filteredRouterList, pathname)

    return (
        <Sheet
            className="Sidebar"
            sx={{
                position: { xs: 'fixed', md: 'sticky' },
                transform: {
                    xs: 'translateX(calc(100% * (var(--SideNavigation-slideIn, 0) - 1)))',
                    md: 'none',
                },
                transition: 'transform 0.4s, width 0.4s',
                // App-chrome stacking scale, kept below Joy's own scale (styles/extendTheme.js: popup:1000, modal:1300,
                // snackbar:1400, tooltip:1500) — the sidebar/header must never outrank a Modal/Popup/Snackbar/Tooltip.
                // Order preserved from before: content < Header (900) < mobile overlay (950) < Sidebar (990) < Joy popup/modal.
                zIndex: 990,
                height: '100dvh',
                width: 'var(--Sidebar-width)',
                top: 0,
                p: 2,
                flexShrink: 0,
                display: 'flex',
                flexDirection: 'column',
                gap: 2,
                borderRight: '1px solid',
                borderColor: 'divider',
            }}
        >
            <GlobalStyles
                styles={(theme) => ({
                    ':root': {
                        '--Sidebar-width': '220px',
                        [theme.breakpoints.up('lg')]: {
                            '--Sidebar-width': '240px',
                        },
                    },
                })}
            />
            <Box
                className="Sidebar-overlay"
                sx={{
                    position: 'fixed',
                    zIndex: 950,
                    top: 0,
                    left: 0,
                    width: '100vw',
                    height: '100vh',
                    opacity: 'var(--SideNavigation-slideIn)',
                    backgroundColor: 'var(--joy-palette-background-backdrop)',
                    transition: 'opacity 0.4s',
                    transform: {
                        xs: 'translateX(calc(100% * (var(--SideNavigation-slideIn, 0) - 1) + var(--SideNavigation-slideIn, 0) * var(--Sidebar-width, 0px)))',
                        lg: 'translateX(-100%)',
                    },
                }}
            />
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                <IconButton variant="soft" color="primary" size="sm" onClick={() => navigate('/projects')}>
                    <img alt="logo" src="/favicon.png" width="28" height="28" />
                </IconButton>
                <Typography level="title-lg">Numeo.ai</Typography>
                <ColorSchemeToggle sx={{ ml: 'auto' }} />
            </Box>
            <Box
                sx={{
                    minHeight: 0,
                    overflow: 'hidden auto',
                    flexGrow: 1,
                    display: 'flex',
                    flexDirection: 'column',
                    [`& .${listItemButtonClasses.root}`]: {
                        gap: 1.5,
                    },
                }}
            >
                <List
                    size="sm"
                    sx={{
                        gap: 1,
                        '--List-nestedInsetStart': '30px',
                        '--ListItem-radius': (theme) => theme.vars.radius.sm,
                    }}
                >
                    {filteredRouterList.map((item) => (
                        <SidebarItem key={item.name} item={item} pathname={pathname} activePath={activePath} />
                    ))}
                </List>
            </Box>
            <Divider />
            <Box>
                <Typography level="body-lg" textColor="text.tertiary" style={{ textTransform: 'uppercase', textAlign: 'center' }}>
                    {authData!}
                </Typography>
            </Box>
            {/* Environment Display */}
            <Box sx={{ display: 'flex', justifyContent: 'center', mb: 1 }}>
                <Typography
                    level="body-sm"
                    sx={{
                        px: 2,
                        py: 0.5,
                        borderRadius: 'sm',
                        border: '1px solid',
                        borderColor: 'primary.outlinedBorder',
                        color: 'primary.500',
                        backgroundColor: 'transparent',
                        fontWeight: 'md',
                        textTransform: 'capitalize',
                    }}
                >
                    {getStoredEnvironment()}
                </Typography>
            </Box>

            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                <List
                    size="sm"
                    sx={{
                        gap: 1,
                        '--List-nestedInsetStart': '30px',
                        '--ListItem-radius': (theme) => theme.vars.radius.sm,
                    }}
                >
                    <ListItem>
                        <ListItemButton onClick={logout}>
                            <LogoutRoundedIcon />
                            <ListItemContent>
                                <Typography level="title-sm">Logout</Typography>
                            </ListItemContent>
                        </ListItemButton>
                    </ListItem>
                </List>
            </Box>
        </Sheet>
    )
}
